import { defineField, defineType } from "sanity";

export const shopPage = defineType({
  name: "shopPage",
  title: "Shop Page",
  type: "document",
  groups: [
    { name: "content", title: "Content", default: true },
    { name: "seo", title: "SEO" }
  ],
  fields: [
    defineField({
      name: "heroImage",
      title: "Hero image",
      type: "image",
      description: "Top hero image for the Shop page.",
      options: { hotspot: true },
      fields: [
        defineField({
          name: "alt",
          title: "Alt text",
          type: "string",
          description: "Describe the image for screen readers.",
          validation: (Rule) =>
            Rule.custom((value) => (value ? true : "Add alt text so the image is accessible.")).warning(
              "Add alt text so the image is accessible."
            )
        })
      ],
      group: "content"
    }),
    defineField({
      name: "heroHeading",
      title: "Hero heading",
      type: "string",
      description: "Heading overlaid on the hero image (e.g. 'Browse the collection').",
      group: "content"
    }),
    defineField({
      name: "swipeDeckCtaText",
      title: "Swipe deck CTA text",
      type: "string",
      description: "Text for the swipe deck button (e.g. 'Browse the Swipe Deck').",
      group: "content"
    }),
    defineField({
      name: "categoryLabel",
      title: "Category section label",
      type: "string",
      description: "Label above the category links (e.g. 'Shop by category').",
      group: "content"
    }),
    defineField({
      name: "stoneLabel",
      title: "Stone section label",
      type: "string",
      description: "Label above the stone links (e.g. 'Shop by stone').",
      group: "content"
    }),
    defineField({
      name: "topHitsLabel",
      title: "Top hits label",
      type: "string",
      description: "Label above the top hits section (e.g. 'Top Hits').",
      group: "content"
    }),
    defineField({
      name: "allPiecesLabel",
      title: "All pieces label",
      type: "string",
      description: "Label above the full catalog grid (e.g. 'Shop all pieces').",
      group: "content"
    }),
    defineField({
      name: "featuredStones",
      title: "Featured stones",
      type: "array",
      description: "Which gemstones appear in the 'Shop by stone' section. Add, remove, or reorder as needed.",
      of: [{ type: "string" }],
      group: "content"
    }),
    defineField({
      name: "shopGallery",
      title: "Shop page gallery",
      type: "array",
      description: "Photos that appear in the slideshow at the bottom of the shop page.",
      of: [
        {
          type: "image",
          options: { hotspot: true },
          fields: [
            defineField({ name: "alt", title: "Alt text", type: "string" })
          ]
        }
      ],
      group: "content"
    }),
    defineField({
      name: "metaTitle",
      title: "Meta title",
      type: "string",
      description: "Page title for search engines (e.g. 'Shop | Vault 88').",
      group: "seo"
    }),
    defineField({
      name: "metaDescription",
      title: "Meta description",
      type: "text",
      rows: 3,
      description: "Short description for search engines.",
      group: "seo"
    }),
    defineField({
      name: "ogImage",
      title: "Sharing image",
      type: "image",
      description: "Image shown when this page is shared on social media.",
      options: { hotspot: true },
      fields: [
        defineField({
          name: "alt",
          title: "Alt text",
          type: "string",
          description: "Describe the image for screen readers."
        })
      ],
      group: "seo"
    })
  ],
  preview: {
    prepare: () => ({
      title: "Shop Page"
    })
  }
});
